import React from 'react';

/**
 * Compact paragraph-level audio indicator + click-to-play trigger.
 * - When this paragraph is active AND the player is playing → animated waveform (oscillating bars)
 * - When this paragraph is active but paused → static blue speaker
 * - When this paragraph is inactive → muted gray speaker
 *
 * The global play/pause button lives in the bottom audio bar, so this one's
 * primary role is "show me which paragraph is currently being read".
 */
const AudioPlayButton = ({ isActive, isPlaying, onClick }) => {
    const playingHere = isActive && isPlaying;
    const pausedHere = isActive && !isPlaying;

    return (
        <button
            onClick={onClick}
            type="button"
            title={playingHere ? 'Pause' : 'Play'}
            className={`inline-flex items-center justify-center w-6 h-6 rounded-full transition-colors flex-shrink-0 ${
                playingHere
                    ? 'text-blue-600 dark:text-blue-400'
                    : pausedHere
                        ? 'text-blue-500/80 dark:text-blue-400/80 hover:text-blue-600 dark:hover:text-blue-400'
                        : 'text-gray-400 dark:text-gray-500 hover:text-blue-500 dark:hover:text-blue-400'
            }`}
        >
            {playingHere ? (
                /* Animated oscillating bars (equalizer style) */
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                    <rect x="3"  y="7" width="2.5" height="6" rx="1.25">
                        <animate attributeName="height" values="3;10;3" dur="0.8s" repeatCount="indefinite" />
                        <animate attributeName="y"      values="8.5;5;8.5" dur="0.8s" repeatCount="indefinite" />
                    </rect>
                    <rect x="8.75" y="4" width="2.5" height="12" rx="1.25">
                        <animate attributeName="height" values="6;14;6" dur="0.8s" begin="0.2s" repeatCount="indefinite" />
                        <animate attributeName="y"      values="7;3;7"   dur="0.8s" begin="0.2s" repeatCount="indefinite" />
                    </rect>
                    <rect x="14.5" y="6" width="2.5" height="8" rx="1.25">
                        <animate attributeName="height" values="4;11;4" dur="0.8s" begin="0.4s" repeatCount="indefinite" />
                        <animate attributeName="y"      values="8;4.5;8" dur="0.8s" begin="0.4s" repeatCount="indefinite" />
                    </rect>
                </svg>
            ) : (
                /* Static speaker icon */
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M11 5L6 9H2v6h4l5 4V5z" />
                    <path d="M15.54 8.46a5 5 0 010 7.07" opacity={pausedHere ? 1 : 0.5} />
                </svg>
            )}
        </button>
    );
};

export default AudioPlayButton;
